import React, { useMemo, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { useClickOutside } from '../hooks';
import {
  TaskStatus,
  TaskStatusLabels,
  TaskStatusColors,
  Priority,
  PriorityLabels,
  PriorityColors,
  VALID_TRANSITIONS,
  SUPERVISOR_ONLY_STATUSES,
  TaskFile,
  StatusModalState,
  FeedItem,
} from '../types';
import { getSubElementPath, calculateActualHours } from '../data/mockData';
import { FEATURES } from '../config/features';
import { formatHours, formatDateShort, isOverdue, getEffectivePriority, isAutoEscalated, validateDateRange, validateEstimatedHours, getTaskProgress } from '../utils/format';
import { createLogger } from '../utils/logger';
import { CheckCircleIcon } from '../components/icons/Icons';
import { validateFile } from '../lib/storageHelpers';
import { useToast } from '../contexts/ToastContext';
import { AttachmentPreviewModal } from './task-detail/AttachmentPreviewModal';
import { StatusChangeModal } from './task-detail/StatusChangeModal';
import { TaskStatusSelector } from './task-detail/TaskStatusSelector';
import { TaskInfoCard } from './task-detail/TaskInfoCard';
import { TimeTrackingCard } from './task-detail/TimeTrackingCard';
import { AssignedPersonnelCard } from './task-detail/AssignedPersonnelCard';
import { DescriptionCard } from './task-detail/DescriptionCard';
import { AttachmentsCard } from './task-detail/AttachmentsCard';
import { ActivityFeed } from './task-detail/ActivityFeed';
import { ChangeLogCard } from './task-detail/ChangeLogCard';
import { ExtraWorkCard } from './task-detail/ExtraWorkCard';
import './TaskDetailPage.css';

const logger = createLogger('TaskDetailPage');

export const TaskDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isProjectManager } = useAuth();
  const { activeProject, activeProjectMembers, allUsers, updateTaskStatus, addTaskComment, updateSubElementFields, addTaskAttachmentFromFiles, removeTaskAttachment, startTimeEntry, stopTimeEntry, markAsExtraWork, updateTaskComment, deleteTaskComment, moveSubElement } = useProjects();
  const { showToast } = useToast();

  const [statusModal, setStatusModal] = useState<StatusModalState>({ isOpen: false, newStatus: null });
  const [previewAttachment, setPreviewAttachment] = useState<TaskFile | null>(null);
  const [newComment, setNewComment] = useState('');
  const [activeTab, setActiveTab] = useState<'activity' | 'history'>('activity');
  const [movingLocation, setMovingLocation] = useState(false);
  const [draftElementId, setDraftElementId] = useState('');
  const [attachError, setAttachError] = useState('');
  const [uploadError, setUploadError] = useState('');

  // Editing states
  const [editingDescription, setEditingDescription] = useState(false);
  const [draftDescription, setDraftDescription] = useState('');
  const [editingHours, setEditingHours] = useState(false);
  const [draftHours, setDraftHours] = useState('');
  const [editingAssigned, setEditingAssigned] = useState(false);
  const [draftAssigned, setDraftAssigned] = useState<string[]>([]);
  const [editingDates, setEditingDates] = useState(false);
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [editingPriority, setEditingPriority] = useState(false);
  const priorityPickerRef = useRef<HTMLDivElement>(null);

  useClickOutside(priorityPickerRef, () => setEditingPriority(false), editingPriority);

  const savePriority = (newPriority: Priority) => {
    if (!taskId) return;
    updateSubElementFields(taskId, { priority: newPriority });
    showToast(`Priority updated to ${PriorityLabels[newPriority]}`, 'success');
    setEditingPriority(false);
  };

  const techUsers = activeProjectMembers;

  // Find the task in the active project
  const taskData = useMemo(() => {
    if (!activeProject || !taskId) return null;
    return getSubElementPath(activeProject, taskId);
  }, [activeProject, taskId]);

  const task = taskData?.subElement;

  const handleStatusChange = (newStatus: TaskStatus) => {
    logger.debug(`[UI] handleStatusChange: newStatus=${newStatus} taskId=${taskId?.slice(0,8)}`);
    if (newStatus === 'FINISHED' || newStatus === 'ISSUES' || newStatus === 'PAUSED') {
      logger.debug(`[UI] Opening StatusChangeModal for ${newStatus}`);
      setStatusModal({ isOpen: true, newStatus });
      return;
    }
    // STARTED / IN_PROGRESS: start timers for ALL assigned team members
    if (FEATURES.TIME_TRACKING && (newStatus === 'STARTED' || newStatus === 'IN_PROGRESS') && task) {
      const members = task.assignedTo.length > 0 ? task.assignedTo : (currentUser ? [currentUser.id] : []);
      logger.debug(`[UI] Starting timers for ${members.length} members:`, members.map(id => id.slice(0,8)));
      members.forEach(uid => startTimeEntry(taskId!, uid));
    }
    updateTaskStatus(taskId!, newStatus, undefined, currentUser?.id).then(result => {
      if (result.success) {
        showToast(`Task status changed to ${TaskStatusLabels[newStatus]}`, 'success');
      } else {
        showToast(result.error || 'Could not change the task status.', 'error');
      }
    });
  };

  const handleModalConfirm = async (comment: string, photoFiles?: File[]): Promise<{ success: boolean; error?: string }> => {
    logger.debug(`[UI] handleModalConfirm: status=${statusModal.newStatus} comment="${comment?.slice(0,30)}" photos=${photoFiles?.length || 0}`);
    const target = statusModal.newStatus!;

    // Wait for the server before showing success or closing, so a rejected
    // change no longer silently reverts a few seconds later.
    const result = await updateTaskStatus(taskId!, target, comment || undefined, currentUser?.id, photoFiles);
    if (!result.success) {
      showToast(result.error || 'Could not change the task status.', 'error');
      return result; // keep the modal open so the user can retry
    }

    // Only stop timers once the status change is confirmed.
    if (FEATURES.TIME_TRACKING && (target === 'PAUSED' || target === 'FINISHED' || target === 'ISSUES') && task) {
      const members = task.assignedTo.length > 0 ? task.assignedTo : (currentUser ? [currentUser.id] : []);
      members.forEach(uid => stopTimeEntry(taskId!, uid, comment));
    }

    const toastType = target === 'ISSUES' ? 'warning' : target === 'FINISHED' ? 'success' : 'info';
    showToast(`Task status changed to ${TaskStatusLabels[target]}`, toastType);
    setStatusModal({ isOpen: false, newStatus: null });
    return result;
  };

  const closeModal = () => {
    setStatusModal({ isOpen: false, newStatus: null });
  };

  const handleAddComment = async (files?: File[]) => {
    logger.debug(`[UI] handleAddComment: text="${newComment?.slice(0,30)}" files=${files?.length || 0}`);
    if ((!newComment.trim() && (!files || files.length === 0)) || !currentUser || !taskId) {
      logger.debug(`[UI] handleAddComment: SKIP - empty comment or no user/taskId`);
      return;
    }

    setUploadError('');
    let commentAttachments: TaskFile[] = [];
    if (files && files.length > 0) {
      logger.debug(`[UI] handleAddComment: uploading ${files.length} files first...`);
      try {
        // Las imágenes van como comment_photo; los documentos (PDF, planos)
        // como 'document', que es el tipo que permite validarlos y subirlos.
        const images = files.filter(f => f.type.startsWith('image/'));
        const documents = files.filter(f => !f.type.startsWith('image/'));
        const uploaded: TaskFile[] = [];
        if (images.length > 0) {
          uploaded.push(...await addTaskAttachmentFromFiles(taskId, images, 'comment_photo', currentUser.id));
        }
        if (documents.length > 0) {
          uploaded.push(...await addTaskAttachmentFromFiles(taskId, documents, 'document', currentUser.id));
        }
        commentAttachments = uploaded;
        logger.debug(`[UI] handleAddComment: got ${commentAttachments.length} attachments back`);
      } catch (err: unknown) {
        logger.error(`[UI] handleAddComment: upload failed`, err);
        setUploadError(err instanceof Error ? err.message : 'Error al subir los archivos. Intenta de nuevo.');
        return;
      }
    }

    logger.debug(`[UI] handleAddComment: calling addTaskComment with ${commentAttachments.length} attachments`);
    await addTaskComment(taskId, {
      userId: currentUser.id,
      userName: currentUser.name,
      content: newComment.trim(),
      attachments: commentAttachments,
    });
    logger.debug(`[UI] handleAddComment: DONE`);
    setNewComment('');
    showToast('Comment added', 'success');
  };

  // ==================== Edit handlers ====================
  const startEditDescription = () => {
    if (!task) return;
    setDraftDescription(task.description || '');
    setEditingDescription(true);
  };
  const saveDescription = () => {
    if (!taskId) return;
    updateSubElementFields(taskId, { description: draftDescription.trim() });
    setEditingDescription(false);
    showToast('Description saved', 'success');
  };

  const startEditHours = () => {
    if (!task) return;
    setDraftHours(String(task.estimatedHours));
    setEditingHours(true);
  };
  const saveHours = () => {
    if (!taskId) return;
    const validationError = validateEstimatedHours(draftHours);
    if (validationError) {
      showToast(validationError, 'error');
      return;
    }
    const val = Number(draftHours);
    updateSubElementFields(taskId, { estimatedHours: val });
    showToast('Hours updated', 'success');
    setEditingHours(false);
  };

  const startEditAssigned = () => {
    if (!task) return;
    setDraftAssigned([...task.assignedTo]);
    setEditingAssigned(true);
  };
  const toggleAssignee = (uid: string) => {
    setDraftAssigned(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };
  const saveAssigned = () => {
    if (!taskId) return;
    updateSubElementFields(taskId, { assignedTo: draftAssigned });
    setEditingAssigned(false);
    showToast('Assignees updated', 'success');
  };

  const startEditDates = () => {
    if (!task) return;
    setDraftStartDate(task.startDate || '');
    setDraftEndDate(task.endDate || '');
    setEditingDates(true);
  };
  const saveDates = () => {
    if (!taskId) return;
    const validationError = validateDateRange(draftStartDate, draftEndDate);
    if (validationError) {
      showToast(validationError, 'error');
      return;
    }
    updateSubElementFields(taskId, { startDate: draftStartDate, endDate: draftEndDate });
    setEditingDates(false);
    showToast('Dates updated', 'success');
  };

  const handleRemoveAssignee = (uid: string) => {
    if (!taskId || !task) return;
    updateSubElementFields(taskId, { assignedTo: task.assignedTo.filter(id => id !== uid) });
    showToast('Assignees updated', 'success');
  };

  const handleAddAttachment = () => {
    if (!taskId || !currentUser) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.multiple = true;
    input.onchange = (ev: Event) => {
      const files = Array.from((ev.target as HTMLInputElement).files || []);
      if (files.length === 0) return;
      const errors: string[] = [];
      const validFiles: File[] = [];
      files.forEach(f => {
        const err = validateFile(f, 'work_photo');
        if (err) errors.push(`${err.file}: ${err.reason}`);
        else validFiles.push(f);
      });
      if (errors.length > 0) setAttachError(errors.join(' | '));
      else setAttachError('');
      if (validFiles.length > 0) {
        addTaskAttachmentFromFiles(taskId, validFiles, 'work_photo', currentUser.id)
          .then(() => showToast(validFiles.length > 1 ? 'Attachments uploaded' : 'Attachment uploaded', 'success'))
          .catch((err: Error) => setAttachError(err.message || 'Error uploading attachment.'));
      }
    };
    input.click();
  };

  const handleRemoveAttachment = (attId: string) => {
    if (!taskId) return;
    removeTaskAttachment(taskId, attId);
    showToast('Attachment removed', 'info');
  };

  // ==================== Build unified activity feed ====================

  const activityFeed: FeedItem[] = useMemo(() => {
    if (!task) return [];
    const items: FeedItem[] = [];

    const statusCommentContents = new Set<string>();
    (task.statusHistory || []).forEach(h => {
      if (h.comment) statusCommentContents.add(`${h.comment}::${h.timestamp}`);
    });

    task.comments.forEach(c => {
      const isDuplicate = statusCommentContents.has(`${c.content}::${c.createdAt}`);
      if (!isDuplicate) {
        items.push({ type: 'comment', id: c.id, timestamp: c.createdAt, userName: c.userName, content: c.content, attachments: c.attachments.length > 0 ? c.attachments : undefined, userId: c.userId, editedAt: c.editedAt });
      }
    });
    // Solo cambios de estado, como hasta ahora. Las ediciones de campos
    // van a la pestaña Historial para no sepultar los comentarios aquí.
    (task.statusHistory || []).filter(h => h.field === 'status').forEach(h => {
      items.push({ type: 'status', id: h.id, timestamp: h.timestamp, changedByName: h.changedByName, field: h.field, oldValue: h.oldValue, newValue: h.newValue, comment: h.comment, attachmentIds: h.attachmentIds });
    });
    items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return items;
  }, [task]);

  // ==================== Time breakdown by member ====================
  const timeBreakdown = useMemo(() => {
    if (!task) return [];
    const byUser: Record<string, number> = {};
    task.timeEntries.forEach(te => {
      if (!te.endTime) return;
      const ms = new Date(te.endTime).getTime() - new Date(te.startTime).getTime();
      const hours = (ms / 3600000) - (te.pausedDuration || 0) / 60;
      byUser[te.userId] = (byUser[te.userId] || 0) + Math.max(0, hours);
    });
    return Object.entries(byUser)
      .map(([userId, hours]) => ({
        userId,
        userName: allUsers.find(u => u.id === userId)?.name || techUsers.find(u => u.id === userId)?.name || 'Unknown',
        hours: Math.round(hours * 100) / 100,
      }))
      .filter(e => e.hours > 0);
  }, [task, allUsers, techUsers]);

  // ==================== Actual hours from time entries (unified source) ====================
  const actualHours = useMemo(() => {
    if (!task) return 0;
    return calculateActualHours(task.timeEntries);
  }, [task]);

  // Determine back path
  const backPath = location.pathname.startsWith('/my-tasks') ? '/my-tasks' : location.pathname.startsWith('/calendar') ? '/calendar' : '/workspace';
  const backLabel = location.pathname.startsWith('/my-tasks') ? '\u2190 Back to My Tasks' : location.pathname.startsWith('/calendar') ? '\u2190 Back to Calendar' : '\u2190 Back to Workspace';

  if (!activeProject) {
    return (
      <div className="detail-page">
        <div className="detail-empty">
          <h2>No Active Project</h2>
          <p>Please select a project from the sidebar.</p>
        </div>
      </div>
    );
  }

  if (!task || !taskData) {
    return (
      <div className="detail-page">
        <div className="detail-empty">
          <h2>Task Not Found</h2>
          <p>The requested task does not exist in the current project.</p>
          <button className="back-btn" onClick={() => navigate(backPath)}>
            {backLabel}
          </button>
        </div>
      </div>
    );
  }

  const canAccessTask = isProjectManager || (!!currentUser && task.assignedTo.includes(currentUser.id));
  if (!canAccessTask) {
    return (
      <div className="detail-page">
        <div className="detail-empty detail-access-denied">
          <h2>Task Access Restricted</h2>
          <p>This task is not assigned to you. Contact a Project Manager if you need access.</p>
          <button className="back-btn" onClick={() => navigate(backPath)}>
            {backLabel}
          </button>
        </div>
      </div>
    );
  }

  const hoursOverBudget = actualHours > task.estimatedHours;
  const taskPath = `${taskData.zone.code} \u2014 ${taskData.zone.name} > ${taskData.section.code} \u2014 ${taskData.section.name} > ${taskData.element.code} \u2014 ${taskData.element.name}`;
  // Abrir y resolver revisiones es exclusivo del supervisor; la BD lo rechazaría
  // igualmente, pero así el técnico no ve opciones que no puede usar. El resto de
  // su flujo (incluido cerrar la tarea) queda intacto.
  const validTransitions = (VALID_TRANSITIONS[task.status] || []).filter(next => {
    if (isProjectManager) return true;
    if (SUPERVISOR_ONLY_STATUSES.includes(task.status)) return false;
    return !SUPERVISOR_ONLY_STATUSES.includes(next);
  });
  const taskOverdue = isOverdue(task.endDate, task.status);
  const taskProgress = getTaskProgress(task);
  const changeLogCount = (task.statusHistory || []).filter(h => h.field !== 'status').length;

  const locationOptions = activeProject
    ? activeProject.zones.flatMap(z =>
        z.sections.flatMap(s =>
          s.elements.map(e => ({ id: e.id, label: `${z.name} > ${s.name} > ${e.name}` }))
        )
      )
    : [];

  const handleSaveLocation = async () => {
    const target = draftElementId || task.elementId;
    if (target === task.elementId) { setMovingLocation(false); return; }
    const result = await moveSubElement(task.id, target);
    setMovingLocation(false);
    if (!result.success) setAttachError(result.error || 'Could not move the task.');
  };

  return (
    <div className="detail-page">
      {/* Status Change Modal */}
      <StatusChangeModal
        isOpen={statusModal.isOpen}
        newStatus={statusModal.newStatus}
        currentStatus={task.status}
        onConfirm={handleModalConfirm}
        onClose={closeModal}
      />

      {/* Attachment Preview Modal */}
      <AttachmentPreviewModal attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />

      {/* Header */}
      <div className="detail-header">
        <div className="detail-header-left">
          <button className="back-btn" onClick={() => navigate(backPath)}>
            {backLabel}
          </button>
          <div>
            <h1 className="detail-order-number">{task.code}</h1>
            <span className="detail-date">{task.name}</span>
          </div>
        </div>
      </div>

      {/* Status Row */}
      <div className="status-row-detail">
        <div className="status-group">
          <label>Status</label>
          <span
            className="badge large"
            style={{ backgroundColor: TaskStatusColors[task.status], color: '#fff' }}
          >
            {TaskStatusLabels[task.status]}
          </span>
        </div>
        <div className="status-group task-progress-group">
          <label>Progress</label>
          <div className="task-progress-bar" role="progressbar" aria-valuenow={taskProgress} aria-valuemin={0} aria-valuemax={100}>
            <div
              className="task-progress-fill"
              style={{ width: `${taskProgress}%`, backgroundColor: TaskStatusColors[task.status] }}
            />
          </div>
          <span className="task-progress-value">{taskProgress}%</span>
        </div>
        <div className="status-group">
          <label>Priority</label>
          <div className="priority-picker-wrap" ref={priorityPickerRef}>
            <span
              className={`badge large${isProjectManager ? ' priority-badge-clickable' : ''}`}
              style={{ backgroundColor: PriorityColors[getEffectivePriority(task)], color: '#fff' }}
              onClick={() => isProjectManager && setEditingPriority(v => !v)}
              title={
                isAutoEscalated(task)
                  ? `Auto-escalated: ${task.endDate === new Date().toISOString().split('T')[0] ? 'deadline is today' : '1 day to deadline'}${isProjectManager ? ' \u00B7 Click to override' : ''}`
                  : isProjectManager ? 'Click to change priority' : undefined
              }
            >
              {PriorityLabels[getEffectivePriority(task)]}
              {isAutoEscalated(task) && <span className="priority-auto-badge">{'\u2191'}</span>}
              {isProjectManager && <span className="priority-badge-caret">{'\u25BE'}</span>}
            </span>
            {editingPriority && (
              <div className="priority-popover">
                {isAutoEscalated(task) && (
                  <div className="priority-popover-notice">
                    {'Auto-escalated \u00B7 Set manually to override'}
                  </div>
                )}
                {(Object.keys(PriorityLabels) as Priority[]).map(p => (
                  <button
                    key={p}
                    className={`priority-option${p === task.priority ? ' active' : ''}`}
                    style={{ '--priority-color': PriorityColors[p] } as React.CSSProperties}
                    onClick={() => savePriority(p)}
                  >
                    <span className="priority-dot" style={{ backgroundColor: PriorityColors[p] }} />
                    {PriorityLabels[p]}
                    {p === task.priority && <span className="priority-check">{'\u2713'}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="status-group">
          <label>Dates</label>
          <span className={`badge large ${taskOverdue ? 'badge-overdue' : ''}`} style={{ backgroundColor: taskOverdue ? 'var(--accent-red)' : 'var(--surface-2)', color: taskOverdue ? '#fff' : 'var(--text-2)' }}>
            {formatDateShort(task.startDate)} {'\u2014'} {formatDateShort(task.endDate)}
          </span>
        </div>
        {FEATURES.TIME_TRACKING && (
          <div className="total-amount-box">
            <div className="total-amount-item">
              <label>Estimated</label>
              <span className="total-value">{formatHours(task.estimatedHours)}</span>
            </div>
            <div className="total-amount-divider" />
            <div className="total-amount-item">
              <label>Actual</label>
              <span
                className="total-value tabular-nums"
                style={{ color: hoursOverBudget ? 'var(--accent-red)' : 'var(--accent-green)' }}
              >
                {formatHours(actualHours)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ====== ACTIONS ====== */}
      <TaskStatusSelector
        currentStatus={task.status}
        validTransitions={validTransitions}
        onStatusChange={handleStatusChange}
      />

      {/* ====== TASK COMPLETED MESSAGE ====== */}
      {task.status === 'FINISHED' && (
        <div className="detail-card full-width">
          <div className="task-finished-msg">
            <CheckCircleIcon size={24} />
            <span>This task is completed.</span>
          </div>
        </div>
      )}

      {/* ====== Info Cards Grid ====== */}
      <div className="detail-grid">
        <TaskInfoCard
          code={task.code}
          name={task.name}
          taskPath={taskPath}
          destinations={locationOptions}
          movingLocation={movingLocation}
          draftElementId={draftElementId || task.elementId}
          onStartMoveLocation={() => { setDraftElementId(task.elementId); setMovingLocation(true); }}
          onSaveLocation={handleSaveLocation}
          onCancelMoveLocation={() => setMovingLocation(false)}
          onDraftElementIdChange={setDraftElementId}
          startDate={task.startDate}
          endDate={task.endDate}
          createdAt={task.createdAt}
          updatedAt={task.updatedAt}
          taskOverdue={taskOverdue}
          isProjectManager={isProjectManager}
          editingDates={editingDates}
          draftStartDate={draftStartDate}
          draftEndDate={draftEndDate}
          onStartEditDates={startEditDates}
          onSaveDates={saveDates}
          onCancelEditDates={() => setEditingDates(false)}
          onDraftStartDateChange={setDraftStartDate}
          onDraftEndDateChange={setDraftEndDate}
        />

        {FEATURES.TIME_TRACKING && (
          <TimeTrackingCard
            estimatedHours={task.estimatedHours}
            actualHours={actualHours}
            hoursOverBudget={hoursOverBudget}
            timeEntryCount={task.timeEntries.filter(te => !!te.endTime).length}
            isProjectManager={isProjectManager}
            editingHours={editingHours}
            draftHours={draftHours}
            onStartEditHours={startEditHours}
            onSaveHours={saveHours}
            onCancelEditHours={() => setEditingHours(false)}
            onDraftHoursChange={setDraftHours}
            timeBreakdown={timeBreakdown}
          />
        )}

        <AssignedPersonnelCard
          assignedTo={task.assignedTo}
          techUsers={techUsers}
          isProjectManager={isProjectManager}
          editingAssigned={editingAssigned}
          draftAssigned={draftAssigned}
          onStartEditAssigned={startEditAssigned}
          onSaveAssigned={saveAssigned}
          onCancelEditAssigned={() => setEditingAssigned(false)}
          onToggleAssignee={toggleAssignee}
          onRemoveAssignee={isProjectManager ? handleRemoveAssignee : undefined}
        />
      </div>

      {/* ====== Description ====== */}
      <DescriptionCard
        description={task.description}
        isProjectManager={isProjectManager}
        editingDescription={editingDescription}
        draftDescription={draftDescription}
        onStartEditDescription={startEditDescription}
        onSaveDescription={saveDescription}
        onCancelEditDescription={() => setEditingDescription(false)}
        onDraftDescriptionChange={setDraftDescription}
      />

      {/* ====== Attachments ====== */}
      {attachError && (
        <div className="status-modal-error" style={{ marginBottom: '0.5rem' }}>{attachError}</div>
      )}
      <AttachmentsCard
        // Se excluye también lo que cuelga de un comentario: desde que los
        // comentarios aceptan documentos, filtrar solo por tipo los duplicaría aquí.
        attachments={task.attachments.filter(a => a.fileType !== 'comment_photo' && a.fileType !== 'status_photo' && !a.commentId)}
        isProjectManager={isProjectManager}
        onAddAttachment={handleAddAttachment}
        onRemoveAttachment={handleRemoveAttachment}
        onPreviewAttachment={setPreviewAttachment}
        onDropFiles={(files) => {
          if (!taskId || !currentUser) return;
          const errors: string[] = [];
          const validFiles: File[] = [];
          files.forEach(f => {
            const err = validateFile(f, 'work_photo');
            if (err) errors.push(`${err.file}: ${err.reason}`);
            else validFiles.push(f);
          });
          if (errors.length > 0) setAttachError(errors.join(' | '));
          else setAttachError('');
          if (validFiles.length > 0) {
            addTaskAttachmentFromFiles(taskId, validFiles, 'work_photo', currentUser.id)
              .then(() => showToast(validFiles.length > 1 ? 'Attachments uploaded' : 'Attachment uploaded', 'success'))
              .catch((err: Error) => setAttachError(err.message || 'Error uploading attachment.'));
          }
        }}
      />

      <ExtraWorkCard
        task={task}
        isProjectManager={isProjectManager}
        onMarkAsExtraWork={(reason, authorizedBy) => markAsExtraWork(task.id, reason, authorizedBy)}
      />

      {/* ====== Activity Feed ====== */}
      {uploadError && (
        <div className="status-modal-error" style={{ marginBottom: '0.5rem' }}>{uploadError}</div>
      )}

      <div className="detail-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'activity'}
          className={`detail-tab${activeTab === 'activity' ? ' active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          Activity
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'history'}
          className={`detail-tab${activeTab === 'history' ? ' active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Change History{changeLogCount > 0 ? ` (${changeLogCount})` : ''}
        </button>
      </div>

      {activeTab === 'activity' ? (
        <ActivityFeed
          activityFeed={activityFeed}
          taskFiles={task.attachments}
          newComment={newComment}
          onNewCommentChange={setNewComment}
          onAddComment={handleAddComment}
          onPreviewAttachment={setPreviewAttachment}
          currentUserId={currentUser?.id}
          isProjectManager={isProjectManager}
          onEditComment={(commentId, content) => updateTaskComment(task.id, commentId, content)}
          onDeleteComment={commentId => deleteTaskComment(task.id, commentId)}
        />
      ) : (
        <ChangeLogCard history={task.statusHistory || []} />
      )}
    </div>
  );
};
